import {
	type FrontendPlacementMessage,
	type FrontendStage,
} from '@post-office/message-maker-browser/types';

import { useCallback } from 'react';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import {
	PlacementFrontend,
	type PlacementRenderProps,
} from '@post-office/browser--alexstrasza/ai-risk-assessment-quick-action-spotlight';

import { useMountToHtmlElement } from '@post-office/components--post-office-team';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import { Text } from '@atlaskit/primitives/compiled';

import { FormattedMessage } from 'react-intl';

import { messages } from '../../../components/messages';

import { type MessageTemplateContext } from '../../../index';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';

import { SpotlightHeadline } from '@atlaskit/spotlight/headline';

import { SpotlightHeader } from '@atlaskit/spotlight/header';

import { SpotlightFooter } from '@atlaskit/spotlight/footer';

import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';

import { SpotlightControls } from '@atlaskit/spotlight/controls';

import { SpotlightCard } from '@atlaskit/spotlight/card';

import { SpotlightBody } from '@atlaskit/spotlight/body';

import { SpotlightActions } from '@atlaskit/spotlight/actions';

import { PopoverTarget } from '@atlaskit/spotlight/popover-target';

import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import { PopoverContent } from '@atlaskit/spotlight/popover-content';

type RenderProps = MessageTemplateContext & PlacementRenderProps;

export const render: {
	type: 'render';
	validate: (input: unknown) => RenderProps;
	Component: (input: PlacementRenderProps & Record<string, never>) => JSX.Element | null;
} = PlacementFrontend.createFrontendStage({
	type: 'render',
	validate: noValidation<RenderProps>(),
	Component: ({ onDismiss, onClick }: RenderProps) => {
		const { messageDismissed } = useMessageEvent();
		const { onClosed, onCtaActioned } = usePostOfficeUserInteractions();
		const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

		const renderInline = useMountToHtmlElement({
			selectors: '#assess-risk-button-spotlight-target',
			as: 'inline',
			position: 'inside',
		});

		const dismiss = useCallback(() => {
			void messageDismissed();
			void onClosed({ interactionIdentifier: 'spotlight-dismissed' });
			fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'assessRiskButtonSpotlightDismiss',
			});
			onDismiss?.();
		}, [messageDismissed, onClosed, fireAnalyticsEvent, onDismiss]);

		const tryItNow = useCallback(() => {
			void messageDismissed();
			void onCtaActioned({
				interactionIdentifier: 'try-it-now',
				additionalMetadata: { actionTaken: true },
			});
			fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'assessRiskButtonSpotlightTryItNow',
			});
			onClick?.();
			onDismiss?.();
		}, [messageDismissed, onCtaActioned, fireAnalyticsEvent, onClick, onDismiss]);

		return (
			<div>
				{renderInline(
					<PopoverProvider>
						<PopoverTarget>
							<div />
						</PopoverTarget>
						<PopoverContent dismiss={dismiss} placement="bottom-center" isVisible={true}>
							<SpotlightCard>
								<SpotlightHeader>
									<SpotlightHeadline>
										<FormattedMessage {...messages.heading} />
									</SpotlightHeadline>
									<SpotlightControls>
										<SpotlightDismissControl autoFocus={false} />
									</SpotlightControls>
								</SpotlightHeader>
								<SpotlightBody>
									<Text>
										<FormattedMessage {...messages.description} />
									</Text>
								</SpotlightBody>
								<SpotlightFooter>
									<SpotlightActions>
										<SpotlightPrimaryAction onClick={tryItNow}>
											<FormattedMessage {...messages.tryItNowButton} />
										</SpotlightPrimaryAction>
									</SpotlightActions>
								</SpotlightFooter>
							</SpotlightCard>
						</PopoverContent>
					</PopoverProvider>,
				)}
			</div>
		);
	},
});

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: FrontendStage<PlacementRenderProps>;
}> = PlacementFrontend.createMessageFrontend({
	render,
});
